import React, { createContext, useCallback, useContext, useMemo } from "react";
import { toast, ToastContainer } from "react-toastify";
import PropTypes from 'prop-types';
import 'react-toastify/dist/ReactToastify.min.css';
import AppContext from "context/Context";
import "./ToastProvider.css";

const getDefaultToastOptions = (isDark) => ({
    position: "bottom-right",
    hideProgressBar: false,
    closeOnClick: false,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
    theme: isDark ? "dark" : "light",
});

const ToastContext = createContext(undefined);

export const useToast = () => {
    const context = useContext(ToastContext);
    if (context === undefined) {
        throw new Error("useToast must be used within a ToastProvider");
    }
    return context;
};

export const ToastProvider = ({ children }) => {
    const { config: { isDark } = {} } = useContext(AppContext);

    const showSuccessToast = useCallback(
        (message) => {
            toast.success(message, {
                ...getDefaultToastOptions(isDark),
                autoClose: 4000
            });
        },
        [isDark]
    );

    const showErrorToast = useCallback(
        (message) => {
            toast.error(message, {
                ...getDefaultToastOptions(isDark),
                autoClose: 4000
            });
        },
        [isDark]
    );

    const showWarningToast = useCallback(
        (message) => {
            toast.warning(message, {
                ...getDefaultToastOptions(isDark),
                autoClose: 4000,
            });
        },
        [isDark]
    );

    const showInfoToast = useCallback(
        (message) => {
            toast.info(message, {
                ...getDefaultToastOptions(isDark),
                autoClose: 4000,
            });
        },
        [isDark]
    );

    const hideToast = useCallback((id) => {
        if (id) {
            toast.dismiss(id);
        } else {
            toast.dismiss();
        }
    }, []);

    const contextValue = useMemo(
        () => ({ showSuccessToast, showErrorToast, showWarningToast, showInfoToast, hideToast }),
        [showSuccessToast, showErrorToast, showWarningToast, showInfoToast, hideToast]
    );

    return (
        <ToastContext.Provider value={contextValue}>
            {children}
            <ToastContainer
                position="bottom-right"
                rtl={false}
                theme={isDark ? "dark" : "light"}
            />
        </ToastContext.Provider>
    );
};

ToastProvider.propTypes = {
    children: PropTypes.node.isRequired,
};

export default ToastProvider;
